import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ExpandMore as ExpandMoreIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  PersonOutline as PersonIcon,
  SupportAgent as SupportIcon,
  SwapHoriz as SwitchIcon,
  Workspaces as GroupIcon,
} from "../../components/icons";
import { useMyAccounts, useUserAccessibleAccounts } from "../../../account/hooks";
import HierarchyBreadcrumbs from "../../../context/components/HierarchyBreadcrumbs";
import MasqueradingChip from "./MasqueradingChip";
import { useAuthSession } from "../../../auth/hooks";
import { alpha, createStylesheet, withStyles } from "../../utils/styles";
import { UIAppBar, UIIconButton, UIMenu, UIMenuItem, UIMenuProps, UIToolbar, UITypography } from "../../components";
import SwitchAccountsDialog from "../../../context/components/SwitchAccountsDialog";
import { UserType } from "../../../user-level/types";
import { useContextProvider } from "../../../context/providers/ContextProvider";
import { getFullName } from "../../../user/utils";
import { AccountLevel } from "../../../account/utils";

interface MainProps {
  handleDrawerToggle: () => void;
}

function ToolbarMenu({ handleDrawerToggle }: MainProps) {
  const classes = useStyles();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: session } = useAuthSession();
  const { data: groups } = useMyAccounts();
  const { userType } = useContextProvider();
  const accessibleAccounts = useUserAccessibleAccounts();

  const isAccountLevel = userType == UserType.ACCOUNT;
  const showSwitchGroups = Boolean(session && groups && groups.length > 1) && isAccountLevel;
  const open = Boolean(anchorEl);
  const showSwitchAccounts = accessibleAccounts.length > 1;

  const handleMenu = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goToMyDetailsPage = () => {
    handleClose();
    navigate("/profile");
  };

  const goToSelectGroup = () => {
    handleClose();
    navigate("/groups");
  };

  const goToSwitchAccounts = () => {
    handleClose();
    setIsDialogOpen(true);
  };

  const handleContactSupport = () => {
    handleClose();
    navigate("/contact-support");
  };

  const handleLogout = () => {
    handleClose();
    navigate("/logout");
  };

  return (
    <UIAppBar position="fixed" className={classes.appBar} data-cy="app-bar">
      <UIToolbar className={classes.toolbar}>
        <UIIconButton
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          className={classes.menuButton}
          size="large"
        >
          <MenuIcon />
        </UIIconButton>
        <HierarchyBreadcrumbs />
        <div>
          <UITypography
            data-cy="menu-appbar"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            variant="subtitle2"
            color="textPrimary"
            className={classes.userName}
            noWrap
          >
            <span data-cy="menu-username">{getFullName(session?.userProfile)}</span>
            <MasqueradingChip lite placement="left-start" className={classes.masquerade} />
            <ExpandMoreIcon />
          </UITypography>
          <StyledMenu anchorEl={anchorEl} onClose={handleClose} open={open}>
            <StyledMenuItem onClick={goToMyDetailsPage} data-cy="my-details-link">
              <PersonIcon fontSize="small" />
              My Details
            </StyledMenuItem>
            {showSwitchGroups && (
              <StyledMenuItem onClick={goToSelectGroup} data-cy="groups-link">
                <GroupIcon fontSize="small" />
                Select {AccountLevel.Group.label}
              </StyledMenuItem>
            )}
            {showSwitchAccounts && (
              <StyledMenuItem onClick={goToSwitchAccounts} data-cy="accounts-link">
                <SwitchIcon fontSize="small" />
                Switch {AccountLevel.Account.label}s
              </StyledMenuItem>
            )}
            {isAccountLevel && (
              <StyledMenuItem onClick={handleContactSupport} data-cy="contact-support-link">
                <SupportIcon fontSize="small" />
                Contact Support
              </StyledMenuItem>
            )}

            <StyledMenuItem onClick={handleLogout} data-cy="logout-link">
              <LogoutIcon fontSize="small" />
              Log Out
            </StyledMenuItem>
          </StyledMenu>
        </div>
      </UIToolbar>
      {showSwitchAccounts && <SwitchAccountsDialog title="Switch Accounts" open={isDialogOpen} setOpen={setIsDialogOpen} />}
    </UIAppBar>
  );
}

const StyledMenu = withStyles(
  (props: UIMenuProps) => (
    <UIMenu
      id="menu-appbar"
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      {...props}
    />
  ),
  {
    paper: {
      width: "249px",
      marginTop: "35px",
    },
  },
);

const StyledMenuItem = withStyles(UIMenuItem, (theme) => ({
  root: {
    fontSize: "14px",
    borderRadius: 6,
    margin: `2px ${theme.spacing(1)}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "& svg": {
      color: theme.palette.text.secondary,
    },
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },
}));

const useStyles = createStylesheet((theme) => ({
  appBar: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: "none",
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    flexGrow: 1,
    maxHeight: "64px",
    overflow: "hidden",
  },
  menuButton: {
    marginRight: "auto",
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },
  toolbar: {
    justifyContent: "flex-end",
    paddingRight: theme.spacing(2),
  },
  userName: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    fontWeight: "bold",
  },
  masquerade: { marginLeft: "5px !important" },
}));

export default ToolbarMenu;
